import * as React from 'react';
import { Helmet } from 'react-helmet';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import AppAppBar from '../components/AppAppBar';
import Footer from '../components/Footer';
import Pricing from '../components/Pricing';

const BRAND_YELLOW = '#ffca18';
const baseUrl = 'https://www.getmarter.com';
const canonical = `${baseUrl}/pricing`;

const faqs = [
  {
    q: 'How much does Marter cost?',
    a: 'Marter costs $24 per month — one plan with everything included: product research, price history, sales data, alerts, the Chrome extension, and the mobile and desktop apps. Paying annually costs $240 per year, which saves $48 compared to twelve monthly payments.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes. Every new account starts with a 7-day free trial with full access to all features. No long-term commitment — you can cancel during the trial.',
  },
  {
    q: 'Are there different plans or tiers?',
    a: 'No tiers. Marter has a single plan, so every subscriber gets every feature: profit calculator, historical price data, seller data, 2 Chrome extension installs, 2 mobile app installs, the desktop app, 10 free alerts, and email support.',
  },
  {
    q: 'How does Marter pricing compare to Keepa?',
    a: 'Keepa costs €29 per month and is built for Amazon. Marter costs $24 per month and is built specifically for Walmart Marketplace, with Walmart price history, Walmart seller data, and a Chrome extension that works on Walmart product pages.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel or manage your subscription at any time from the manage-subscription page — no cancellation fees.',
  },
];

const productSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Marter — Walmart Seller Tool',
  description:
    'All-in-one Walmart marketplace seller tool: product research, price history, sales estimates, stock alerts, Chrome extension, mobile and desktop apps.',
  brand: { '@type': 'Brand', name: 'Marter' },
  offers: [
    {
      '@type': 'Offer',
      name: 'Monthly',
      price: '24',
      priceCurrency: 'USD',
      url: canonical,
      availability: 'https://schema.org/InStock',
    },
    {
      '@type': 'Offer',
      name: 'Annual',
      price: '240',
      priceCurrency: 'USD',
      url: canonical,
      availability: 'https://schema.org/InStock',
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
    { '@type': 'ListItem', position: 2, name: 'Pricing', item: canonical },
  ],
};

export default function PricingPage(props) {
  const defaultTheme = createTheme({ palette: { mode: 'light' } });

  return (
    <ThemeProvider theme={defaultTheme}>
      <Helmet>
        <title>Marter Pricing — $24/Month, Everything Included | Marter</title>
        <meta
          name="description"
          content="Marter costs $24/month — one plan, every feature: Walmart product research, price history, sales data, alerts and Chrome extension. 7-day free trial, cancel anytime."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Marter Pricing — $24/Month, Everything Included" />
        <meta
          property="og:description"
          content="One plan, every feature. Walmart product research, price history, alerts and Chrome extension for $24/month. 7-day free trial."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Marter - Walmart Seller Tool" />
        <meta property="og:image" content={`${baseUrl}/showcase.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <CssBaseline />
      <AppAppBar setEmailModal={props.setEmailModal} />

      <Box sx={{ bgcolor: '#ffffff', pt: { xs: '84px', md: '92px' } }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
            <Chip label="Simple Pricing" size="small" sx={{ bgcolor: BRAND_YELLOW, fontWeight: 700, mb: 2 }} />
            <Typography component="h1" variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
              One Plan. Every Feature. $24/Month.
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', fontWeight: 400 }}>
              No tiers, no add-ons, no surprises. Every Marter subscription includes product
              research, Walmart price history, seller data, alerts, the Chrome extension, and
              the mobile and desktop apps — with a 7-day free trial.
            </Typography>
          </Box>

          <Pricing
            signUp={props.signUp}
            setEmailModal={props.setEmailModal}
            setYearly={props.setYearly}
            silentJim={props.silentJim}
          />

          <Divider sx={{ my: { xs: 4, md: 6 } }} />

          <Box sx={{ maxWidth: 820, mx: 'auto', mb: { xs: 4, md: 6 } }}>
            <Typography component="h2" variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
              Pricing FAQ
            </Typography>
            {faqs.map((f) => (
              <Box key={f.q} sx={{ mb: 3 }}>
                <Typography component="h3" variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {f.q}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {f.a}
                </Typography>
              </Box>
            ))}
            <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
              Comparing tools? See how Marter stacks up against Keepa on our{' '}
              <Link href="/compare/marter-vs-keepa">Marter vs Keepa comparison</Link>, or explore{' '}
              <Link href="/features/price-tracker">price tracking</Link>,{' '}
              <Link href="/features/product-research">product research</Link> and the{' '}
              <Link href="/features/chrome-extension">Chrome extension</Link>.
            </Typography>
          </Box>
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
